import React from "react";
import {Formik} from "formik";
import {validator} from "@reside/forms";
import {
  Modal,
  ModalContent,
  Button,
  SelectField,
  CheckboxItem,
  FieldError,
  Text,
  Badge,
  styled,
} from "@reside/ui";

import {ADMISSION_STATUS_ORDER} from "../../../../atoms/admission-status-tag/AdmissionStatusTag";
import {WorkflowType} from "../../../../utils/enums";
import {WorkflowOption} from "../../../../atoms/start-workflow/types";
import {AdmissionStatusEnum} from "../../../../services/AdmissionsService";
import {WorkflowTriggerModel, WorkflowTriggerRequest} from "./types";
import {TriggerSentence} from "./triggerSentence";

// The form starts with nothing selected (placeholders shown), whereas a submitted
// trigger always has both fields set, hence the widened types here vs. WorkflowTriggerRequest.
type WorkflowTriggerFormValues = Omit<
  WorkflowTriggerRequest,
  "initiatingWorkflowType" | "toStatus"
> &
  Readonly<{
    initiatingWorkflowType: WorkflowType | "";
    toStatus: AdmissionStatusEnum | "";
  }>;

// A status can only be a trigger if it has a preceding status to transition from, so the
// first status in the order (the initial status, with no predecessor) is excluded here.
const TRIGGERING_STATUS_OPTIONS = ADMISSION_STATUS_ORDER.slice(1);

const getResultingWorkflowOptions = (
  workflowOptions: ReadonlyArray<WorkflowOption>,
  initiatingWorkflowType: string,
): ReadonlyArray<WorkflowOption> =>
  workflowOptions.filter(option => option.type !== initiatingWorkflowType);

type Props = Readonly<{
  isOpen: boolean;
  onRequestClose: () => void;
  trigger?: WorkflowTriggerModel;
  templateName: string;
  workflowOptions: ReadonlyArray<WorkflowOption>;
  // Resolves when the trigger is saved; rejects if the backend refuses it.
  onSubmit: (values: WorkflowTriggerRequest) => Promise<void>;
}>;

const buildInitialValues = (
  templateName: string,
  trigger?: WorkflowTriggerModel,
): WorkflowTriggerFormValues => ({
  templateName: trigger?.templateName ?? templateName,
  triggerType: "status_change",
  initiatingWorkflowType: trigger?.initiatingWorkflowType ?? "",
  toStatus: trigger?.toStatus ?? "",
  targetWorkflowTypes: trigger?.targetWorkflowTypes ?? [],
  active: trigger?.active ?? true,
});

export const WorkflowTriggerModal = ({
  isOpen,
  onRequestClose,
  trigger,
  templateName,
  workflowOptions,
  onSubmit,
}: Props) => {
  const isEditing = Boolean(trigger);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      closeButtonPosition="right"
      title={isEditing ? "Edit workflow trigger" : "Add workflow trigger"}
      subtitle={`Choose the initiating workflow, status, and resulting workflows for template ${templateName}.`}
      Content={StyledModalContent}
    >
      {({closeModal, Content, Footer, FooterButtonGroup}) => (
        <Formik<WorkflowTriggerFormValues>
          // Remount per trigger so switching between Add and Edit (or between two triggers)
          // never carries over the previous form's values.
          key={trigger?.id ?? "new"}
          initialValues={buildInitialValues(templateName, trigger)}
          validate={values => {
            const errors = validator.validateAll(values, {
              initiatingWorkflowType: "required",
              toStatus: "required",
            }) as Record<string, string>;

            if (values.targetWorkflowTypes.length === 0) {
              errors.targetWorkflowTypes =
                "At least one resulting workflow must be selected.";
            } else if (
              values.targetWorkflowTypes.includes(
                values.initiatingWorkflowType as WorkflowType,
              )
            ) {
              errors.targetWorkflowTypes =
                "The resulting workflows must be different from the initiating workflow.";
            }

            return errors;
          }}
          onSubmit={async values => {
            try {
              await onSubmit(values as WorkflowTriggerRequest);
              closeModal();
            } catch {
              // The mutation already shows the error toast; keep the modal open so the
              // user can fix the values instead of re-entering them.
            }
          }}
        >
          {({values, setFieldValue, submitForm, isSubmitting}) => {
            const resultingWorkflowOptions = getResultingWorkflowOptions(
              workflowOptions,
              values.initiatingWorkflowType,
            );

            const canSubmit =
              values.initiatingWorkflowType !== "" &&
              values.toStatus !== "" &&
              values.targetWorkflowTypes.length > 0 &&
              !values.targetWorkflowTypes.includes(
                values.initiatingWorkflowType,
              );

            return (
              <>
                <Content data-test-id="workflowTriggerModal">
                  <ScrollableBody>
                    <PreviewBox>
                      <PreviewLabel
                        as="p"
                        fontSize="14px"
                        fontWeight="bold"
                        color="gray100"
                        lineHeight="20px"
                      >
                        PREVIEW
                      </PreviewLabel>
                      <Text as="p" style={{fontSize: "16px"}}>
                        <TriggerSentence
                          trigger={values}
                          workflowOptions={workflowOptions}
                        />
                      </Text>
                    </PreviewBox>

                    <StepHeader>
                      <StepBadge color="lightBlue10">1</StepBadge>
                      <StepBadgeText>INITIATING WORKFLOW</StepBadgeText>
                    </StepHeader>
                    <StepBody>
                      <SelectField
                        label=""
                        placeholder="Select workflow"
                        name="initiatingWorkflowType"
                        options={workflowOptions}
                        getOptionLabel={(option: WorkflowOption) =>
                          option.label
                        }
                        getOptionValue={(option: WorkflowOption) => option.type}
                        onChange={event => {
                          const type = event.target.value;
                          setFieldValue(
                            "targetWorkflowTypes",
                            values.targetWorkflowTypes.filter(
                              targetWorkflowType => targetWorkflowType !== type,
                            ),
                          );
                        }}
                      />
                    </StepBody>

                    <FormSection>
                      <StepHeader>
                        <StepBadge color="lightBlue10">2</StepBadge>
                        <StepBadgeText>TRIGGER STATUS</StepBadgeText>
                      </StepHeader>
                      <StepBody>
                        <SelectField
                          label=""
                          placeholder="Select status"
                          name="toStatus"
                          options={TRIGGERING_STATUS_OPTIONS}
                          getOptionLabel={value => value}
                          getOptionValue={value => value}
                        />
                      </StepBody>
                    </FormSection>

                    <FormSection>
                      <StepHeader>
                        <StepBadge color="lightBlue10">3</StepBadge>
                        <StepBadgeText>RESULTING WORKFLOWS</StepBadgeText>
                      </StepHeader>
                      <StepBody>
                        <ResultingWorkflowList>
                          {resultingWorkflowOptions.map(option => (
                            <CheckboxItem
                              key={option.type}
                              name="targetWorkflowTypes"
                              label={option.label}
                              checked={values.targetWorkflowTypes.includes(
                                option.type as WorkflowType,
                              )}
                              onChange={() => {
                                const isSelected =
                                  values.targetWorkflowTypes.includes(
                                    option.type as WorkflowType,
                                  );
                                setFieldValue(
                                  "targetWorkflowTypes",
                                  isSelected
                                    ? values.targetWorkflowTypes.filter(
                                        targetWorkflowType =>
                                          targetWorkflowType !== option.type,
                                      )
                                    : [
                                        ...values.targetWorkflowTypes,
                                        option.type,
                                      ],
                                );
                              }}
                            />
                          ))}
                        </ResultingWorkflowList>
                        <FieldError name="targetWorkflowTypes" />
                      </StepBody>
                    </FormSection>
                  </ScrollableBody>
                </Content>
                <Footer>
                  <FooterActiveControl>
                    <CheckboxItem
                      name="active"
                      onChange={() => setFieldValue("active", !values.active)}
                      checked={values.active}
                      label="Active on save"
                    />
                  </FooterActiveControl>
                  <FooterButtonGroup>
                    <Button outline color="primary" onClick={closeModal}>
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      disabled={!canSubmit || isSubmitting}
                      onClick={submitForm}
                    >
                      Save
                    </Button>
                  </FooterButtonGroup>
                </Footer>
              </>
            );
          }}
        </Formik>
      )}
    </Modal>
  );
};

const StyledModalContent = styled(ModalContent)`
  flex: 1 1 auto;
  min-height: 0;
  overflow: visible;
  color: ${({theme}) => theme.color.darkBlue100};
`;

const ScrollableBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: visible;
  overflow-y: auto;
`;

const PreviewBox = styled.div`
  padding: 12px 14px;
  margin-bottom: ${({theme}) => theme.space[6]}px;
  border-radius: 4px;
  border: 2px dashed ${({theme}) => theme.color.borderGray};
  background-color: ${({theme}) => theme.color.gray5};
  position: sticky;
  top: 0;
  z-index: 1;
`;

const PreviewLabel = styled(Text)`
  margin-bottom: 6px;
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.space[2]}px;
`;

const StepBadge = styled(Badge)`
  color: ${({theme}) => theme.color.darkBlue70};
  font-weight: 600;
  background-color: ${({theme}) => theme.color.lightBlue10};
  margin-left: ${({theme}) => theme.space[1]}px;
`;

const StepBadgeText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({theme}) => theme.color.darkBlue70};
`;

const StepBody = styled.div`
  margin-top: ${({theme}) => theme.space[4]}px;
`;

const ResultingWorkflowList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({theme}) => theme.space[3]}px;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${({theme}) => theme.space[6]}px;
`;

const FooterActiveControl = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  justify-content: flex-end;
  margin-right: ${({theme}) => theme.space[4]}px;
  label > span {
    padding-left: 14px;
  }
`;
